import { LinkProps } from '@interflora/ui-components/build/common/props'

/**
 * Create a standardized breadcrumb item
 */
const createBreadcrumbItem = (label: string, url: string = ''): LinkProps => ({
  highlight: false,
  icon: '',
  newWindow: false,
  noFollow: false,
  label,
  url,
})

/**
 * Static breadcrumb mapping for pages without API breadcrumbs
 */
const getBreadcrumbMap = (): Record<string, LinkProps[]> => {
  // Base home breadcrumb for all pages
  const homeBreadcrumb = createBreadcrumbItem('Home', '/')

  return {
    '/': [homeBreadcrumb],

    // Account pages
    '/account': [homeBreadcrumb, createBreadcrumbItem('My Account')],
    '/account/address-book': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Address Book'),
    ],
    '/account/address-book/add-new-contact-address': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Address Book', '/account/address-book'),
      createBreadcrumbItem('Add New Contact Address'),
    ],
    '/account/address-book/edit-contact': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Address Book', '/account/address-book'),
      createBreadcrumbItem('Edit Contact'),
    ],
    '/account/change-password': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Change Password'),
    ],
    '/account/communications-preferences': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Communications Preferences'),
    ],
    '/account/order-details': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Order History', '/account/order-history'),
      createBreadcrumbItem('Order Details'),
    ],
    '/account/order-history': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Order History'),
    ],
    '/account/personal-details': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Personal Details'),
    ],
    '/account/reminders': [
      homeBreadcrumb,
      createBreadcrumbItem('My Account', '/account'),
      createBreadcrumbItem('Reminders'),
    ],

    // Other pages
    '/basket': [homeBreadcrumb, createBreadcrumbItem('Basket')],
    '/favourites': [homeBreadcrumb, createBreadcrumbItem('Favourites')],
    '/preferences': [homeBreadcrumb, createBreadcrumbItem('Help', '/faq'), createBreadcrumbItem('Preferences')],
    '/forgot-your-password': [homeBreadcrumb, createBreadcrumbItem('Forgot Your Password')],

    // Error pages
    '/404': [homeBreadcrumb, createBreadcrumbItem('Page Not Found')],
  }
}

/**
 * Generate offline breadcrumbs for pages without API breadcrumbs
 * @param path - The current page path
 * @param canonicalUrl - The canonical URL to check for 404 pages
 * @returns Array of breadcrumb items
 */
export const generateOfflineBreadcrumbs = (path: string, canonicalUrl?: string): LinkProps[] => {
  const homeBreadcrumb = createBreadcrumbItem('Home', '/')
  const pagenotfoundError = canonicalUrl?.includes('/404')
  const breadcrumbMap = getBreadcrumbMap()

  // Handle dynamic paths with parameters (e.g., /account/order-details/12345)
  const pathWithoutParams = path.split('?')[0] // Remove query parameters

  // Special handling for 404 pages
  if (pagenotfoundError) {
    return [homeBreadcrumb, createBreadcrumbItem('Page Not Found')]
  }

  // Check for exact match first
  if (breadcrumbMap[pathWithoutParams]) {
    return breadcrumbMap[pathWithoutParams]
  }

  // Handle dynamic routes by checking parent paths
  for (const [mappedPath, breadcrumbs] of Object.entries(breadcrumbMap)) {
    if (pathWithoutParams.startsWith(mappedPath) && mappedPath !== '/') {
      return breadcrumbs
    }
  }

  // Fallback to just home breadcrumb
  return [homeBreadcrumb]
}

/**
 * Build the complete URL for a breadcrumb item
 * @param link - The breadcrumb link object
 * @param index - The index of the breadcrumb in the list
 * @param links - The complete array of breadcrumb links
 * @param asPath - The current page path
 * @returns The formatted URL string without query parameters
 */
export const buildBreadcrumbUrl = (link: LinkProps, index: number, links: LinkProps[], asPath: string): string => {
  let url = link.url || (index === links.length - 1 ? asPath : '')

  // Strip query parameters for breadcrumb schema URLs
  if (url) {
    url = url.split('?')[0]
  }

  return url?.trim() ?? ''
}
